import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import { Html, Outlines, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';
import type { StageId, Vec3 } from '../data/content';
import { displayToken, valueColor } from '../lib/model';
import { COL_TOP, CUBE, STACK_TOP, TILE_D, TILE_H, cubeY } from '../lib/layout';
import { useLabelFactor, useStaggered } from './sceneHooks';
export type SceneToken = {
  key: string;
  text: string;
  id: number;
  color: string;
  vector: number[];
  /** World position in meaning space. */
  cloudPos: Vec3;
  /** Where a freshly generated token starts before flying into the row. */
  spawnFrom?: Vec3;
};
type Props = {
  index: number;
  token: SceneToken;
  tightX: number;
  spreadX: number;
  width: number;
  stage: StageId;
  selected: boolean;
  dimmed: boolean;
  isLast: boolean;
  onSelect: (index: number) => void;
  reducedMotion: boolean;
};
const NEUTRAL = new THREE.Color('#D3D6E2');
const WHITE = new THREE.Color('#FFFFFF');
const CUBE_GEO = new THREE.BoxGeometry(CUBE, CUBE, CUBE);
const PULSE_GEO = new THREE.SphereGeometry(0.065, 16, 16);
const BEAM_H = STACK_TOP - COL_TOP;
const PULSES = 3;
const COLUMN_STAGES: StageId[] = ['embeddings', 'attention', 'layers', 'prediction'];
const {
  damp
} = THREE.MathUtils;
export function TokenObject({
  index,
  token,
  tightX,
  spreadX,
  width,
  stage,
  selected,
  dimmed,
  isLast,
  onSelect,
  reducedMotion
}: Props) {
  const localStage = useStaggered(stage, reducedMotion ? 0 : index * 55);
  const inCloud = localStage === 'meaning';
  const tight = localStage === 'text';
  const showColumn = COLUMN_STAGES.includes(localStage);
  const showBeam = localStage === 'layers' || localStage === 'prediction';
  const showPulses = localStage === 'layers' || localStage === 'prediction' && isLast;
  const showId = localStage === 'tokens' || localStage === 'embeddings';
  const rowX = tight ? tightX : spreadX;
  const labelFactor = useLabelFactor();
  const [hovered, setHovered] = useState(false);
  const initialTile = useRef<Vec3>(token.spawnFrom ?? [stage === 'text' ? tightX : spreadX, TILE_H / 2 + 1.6, 0]).current;
  const initialRow = useRef<Vec3>([spreadX, 0, 0]).current;
  const tileRef = useRef<THREE.Group>(null);
  const bobRef = useRef<THREE.Group>(null);
  const matRef = useRef<THREE.MeshStandardMaterial>(null);
  const rowRef = useRef<THREE.Group>(null);
  const beamRef = useRef<THREE.Group>(null);
  const cubeRefs = useRef<(THREE.Mesh | null)[]>([]);
  const pulseRefs = useRef<(THREE.Mesh | null)[]>([]);
  const columnAnim = useRef({
    show: false,
    since: 0
  });
  const color = useMemo(() => new THREE.Color(token.color), [token.color]);
  const dimColor = useMemo(() => color.clone().multiplyScalar(0.3), [color]);
  const pulseColor = useMemo(() => color.clone().lerp(WHITE, 0.55), [color]);
  const cubeColors = useMemo(() => token.vector.map(v => new THREE.Color(valueColor(v))), [token.vector]);
  useEffect(() => () => {
    document.body.style.cursor = 'auto';
  }, []);
  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const d = Math.min(delta, 0.05);
    const tile = tileRef.current;
    if (tile) {
      const target: Vec3 = inCloud ? token.cloudPos : [rowX, TILE_H / 2 + (hovered ? 0.08 : 0), 0];
      tile.position.x = damp(tile.position.x, target[0], 4.2, d);
      tile.position.y = damp(tile.position.y, target[1], 4.2, d);
      tile.position.z = damp(tile.position.z, target[2], 4.2, d);
      tile.scale.setScalar(damp(tile.scale.x, inCloud ? 0.62 : 1, 4.2, d));
    }
    if (bobRef.current) {
      bobRef.current.position.y = reducedMotion ? 0 : Math.sin(t * 0.9 + index * 0.7) * 0.025;
    }
    const mat = matRef.current;
    if (mat) {
      const targetColor = tight ? NEUTRAL : dimmed ? dimColor : color;
      mat.color.lerp(targetColor, 1 - Math.exp(-d * 5));
      mat.emissive.copy(mat.color);
      const glow = selected && !tight ? 0.42 : hovered ? 0.26 : 0.1;
      mat.emissiveIntensity = damp(mat.emissiveIntensity, glow, 6, d);
    }
    if (rowRef.current) {
      rowRef.current.position.x = damp(rowRef.current.position.x, spreadX, 4.2, d);
    }
    const anim = columnAnim.current;
    if (anim.show !== showColumn) {
      anim.show = showColumn;
      anim.since = t;
    }
    cubeRefs.current.forEach((cube, k) => {
      if (!cube) return;
      const on = t - anim.since >= k * 0.035 ? anim.show : !anim.show;
      const s = damp(cube.scale.x, on ? 1 : 0.0001, 7, d);
      cube.scale.setScalar(s);
      cube.visible = s > 0.002;
    });
    const beam = beamRef.current;
    if (beam) {
      beam.scale.y = damp(beam.scale.y, showBeam ? 1 : 0.0001, 3.5, d);
      beam.visible = beam.scale.y > 0.002;
    }
    const speed = reducedMotion ? 0.06 : 0.28;
    pulseRefs.current.forEach((pulse, p) => {
      if (!pulse) return;
      const phase = (t * speed + p / PULSES + index * 0.07) % 1;
      pulse.position.set(0, COL_TOP + phase * BEAM_H, 0);
      const s = damp(pulse.scale.x, showPulses ? 1 : 0.0001, 6, d);
      pulse.scale.setScalar(s);
      pulse.visible = s > 0.002;
    });
  });
  function onOver(e: ThreeEvent<PointerEvent>) {
    e.stopPropagation();
    setHovered(true);
    document.body.style.cursor = 'pointer';
  }
  function onOut() {
    setHovered(false);
    document.body.style.cursor = 'auto';
  }
  function onClick(e: ThreeEvent<MouseEvent>) {
    e.stopPropagation();
    onSelect(index);
  }
  const {
    space,
    body
  } = displayToken(token.text);
  return <>
      <group ref={tileRef} position={initialTile} data-id="element-87" data-mp="37ri0egmci3wt">
        <group ref={bobRef} data-id="element-88" data-mp="37pnf3175w9s7">
          <RoundedBox args={[width, TILE_H, TILE_D]} radius={0.08} smoothness={4} onPointerOver={onOver} onPointerOut={onOut} onClick={onClick} data-id="element-89" data-mp="37pdkt1f33ggp">
            <meshStandardMaterial ref={matRef} color={NEUTRAL} emissive={NEUTRAL} emissiveIntensity={0.1} roughness={0.42} metalness={0.05} data-id="element-90" data-mp="2k142i9h2xaub" />
            {selected && !tight && <Outlines thickness={0.03} color={WHITE} toneMapped={false} data-id="element-91" data-mp="2lqreyqhuo2kx" />}
          </RoundedBox>

          {!inCloud && <Html center position={[0, 0, TILE_D / 2 + 0.01]} distanceFactor={8 * labelFactor} zIndexRange={[10, 0]} wrapperClass="pointer-events-none" className="pointer-events-none" data-id="element-92" data-mp="2gspnbh6cyuxu">
              <span className={`select-none whitespace-nowrap font-mono text-[18px] font-semibold leading-none ${dimmed ? 'text-[#0B0C12]/40' : 'text-[#0B0C12]'}`} data-id="element-93" data-mp="230t09rjjrb8b">
                {space && !tight && <span className="opacity-35" data-id="element-94" data-mp="3ehj9ljj9oj65">·</span>}
                {body}
              </span>
            </Html>}

          {inCloud && <Html center position={[0, TILE_H / 2 + 0.6, 0]} distanceFactor={11 * labelFactor} zIndexRange={[10, 0]} wrapperClass="pointer-events-none" className="pointer-events-none" data-id="element-95" data-mp="3gzl834uc32kb">
              <span className="select-none whitespace-nowrap rounded-md border border-white/10 bg-[#0B0C12]/90 px-2 py-0.5 font-mono text-[15px] font-medium" style={{
            color: token.color
          }} data-id="element-96" data-mp="3c08c9ulhil5l">
                {space && <span className="opacity-40" data-id="element-97" data-mp="8jjccrgeq9ow">·</span>}
                {body}
              </span>
            </Html>}

          {showId && <Html center position={[0, -TILE_H / 2 + 0.01, TILE_D / 2 + 0.34]} distanceFactor={8 * labelFactor} zIndexRange={[10, 0]} wrapperClass="pointer-events-none" className="pointer-events-none" data-id="element-98" data-mp="3uz44x1hp2rhb">
              <span className="select-none whitespace-nowrap font-mono text-[13px] text-[#6B7084] animate-[fadeIn_0.6s_ease_both]" data-id="element-99" data-mp="2keohpwj5h66">
                #{token.id}
              </span>
            </Html>}
        </group>
      </group>

      <group ref={rowRef} position={initialRow} data-id="element-100" data-mp="3ozzcrnqsbcss">
        {cubeColors.map((c, k) => <mesh key={k} ref={el => {
        cubeRefs.current[k] = el;
      }} geometry={CUBE_GEO} position={[0, cubeY(k), 0]} scale={0.0001} data-id="element-101" data-mp="3k1ph7c15ea6n" data-mpi={k}>
            <meshStandardMaterial color={c} emissive={c} emissiveIntensity={0.35} roughness={0.5} data-id="element-102" data-mp="ghl3d9e4uvq4" />
          </mesh>)}

        <group ref={beamRef} position={[0, COL_TOP, 0]} scale={[1, 0.0001, 1]} data-id="element-103" data-mp="6l88w9arkdub">
          <mesh position={[0, BEAM_H / 2, 0]} data-id="element-104" data-mp="2wcdbim514k93">
            <cylinderGeometry args={[0.022, 0.022, BEAM_H, 8]} data-id="element-105" data-mp="2rbpdhgidv35d" />
            <meshBasicMaterial color={color} transparent opacity={0.55} data-id="element-106" data-mp="2rae7rb5dnlbm" />
          </mesh>
        </group>

        {Array.from({
        length: PULSES
      }, (_, p) => <mesh key={p} ref={el => {
        pulseRefs.current[p] = el;
      }} geometry={PULSE_GEO} scale={0.0001} data-id="element-107" data-mp="1brzvbpjys0t6">
            <meshBasicMaterial color={pulseColor} toneMapped={false} data-id="element-108" data-mp="1lj2qkaq7plkj" />
          </mesh>)}
      </group>
    </>;
}